import { Y_NUMBER_EPSILON } from '@/diagram-persistence/ysync/y-value-equal';

import { rectAnchorMode } from './rect-tbc';
import { isChainSpaceType } from './tbc-types';
import type { Tbc, TbcPoint, TbcType, Top, TopAttachSnap } from './tbc-types';

/** Which side of a parent a child base rides. Mirrors the package's
 *  `AttachSide`; absent on a binding means 'top'. */
export type AttachSide = NonNullable<TopAttachSnap['side']>;

/** The side a parent-attach binding pins to. Absent ⇒ 'top'. */
export function attachSideOf(snap: TopAttachSnap): AttachSide {
    return snap.side ?? 'top';
}

/** Types whose underside may be worked into: the chain family. Single gate
 *  for every creation site (drag candidates, ghost targets, add-under, menu). */
export function hasUnderside(type: TbcType): boolean {
    return isChainSpaceType(type);
}

/** Center of the rectangle a stitch renders as: the base for center-anchored
 *  types, the base–top midpoint for span types. Non-rect types take
 *  rectAnchorMode's 'center' fallback. */
export function rectCenterPoint(type: TbcType, base: TbcPoint, top: TbcPoint): TbcPoint {
    if (rectAnchorMode(type) === 'span') return { x: (base.x + top.x) / 2, y: (base.y + top.y) / 2 };
    return { x: base.x, y: base.y };
}

/** The underside of a stitch: its top reflected across the rect center.
 *  'chain' (center anchor) ⇒ 2·base − top; 'chain-full' (span) ⇒ base. */
export function undersidePoint(type: TbcType, base: TbcPoint, top: TbcPoint): TbcPoint {
    const c = rectCenterPoint(type, base, top);
    return { x: 2 * c.x - top.x, y: 2 * c.y - top.y };
}

/** Whether a handle at `at` sits on `p`, a point the engine computes for
 *  it. Within Y_NUMBER_EPSILON — the sync layer's own tolerance — is on it,
 *  for two kinds of point. One the engine computes with rounding another
 *  engine can do differently in the last bit: cos/sin (a ring), an
 *  arc-length table (a curve); moving a handle within ε would move
 *  it by this engine's rounding alone. And an underside: IEEE arithmetic on
 *  the parent's stored points, the same on every engine, but not a copy, so
 *  the sync layer has no copy check to mend the split a merge can leave in
 *  it (the child from one replica beside the parent from another); an exact
 *  settle would re-pin it on every repair pass. Either way the sync layer's
 *  ε write-back would hand the move back (spec "ε write-back"). So after
 *  settle or a re-ride such a handle is within ε of its current point: left
 *  where it was, or moved exactly onto it. A copied point (a base on its
 *  parent's top) is the same on every engine, a split in it is mended, and it
 *  must match exactly. */
export function sitsOnComputedPoint(at: TbcPoint, p: TbcPoint): boolean {
    return Math.abs(at.x - p.x) <= Y_NUMBER_EPSILON && Math.abs(at.y - p.y) <= Y_NUMBER_EPSILON;
}

/** World point a child base attached to `top` of `parent` on `side` pins to.
 *  Always a fresh object. */
export function parentTopAnchor(parent: Tbc, top: Top, side: AttachSide): TbcPoint {
    if (side === 'top') return { x: top.point.x, y: top.point.y };
    // `bases[0]` is the whole rect: an underside only exists on the chain
    // family (hasUnderside), which is rect-kind and therefore single-base and
    // single-top, so there is no other base this could mean.
    return undersidePoint(parent.type, parent.bases[0].point, top.point);
}
